import { forwardRef, useCallback, useEffect, useImperativeHandle, useMemo, useRef, useState } from 'react';
import { Field, useField, useFormikContext } from 'formik';
import { Badge, Flex, Spinner } from '@chakra-ui/react';

import { useUnitsAndMeasures } from 'newui/store';
import { RfxQuoteHeader } from 'newui/features/Rfx/Submission/components/QuoteTab/RfxQuoteHeader';
import { QuoteComment } from './QuoteComment';
import { QuoteSupportingDocuments } from './QuoteSupportingDocuments';
import { SupplierRowsSection } from './SupplierRowsSection';
import type { SupplierRowsSectionHandle } from './SupplierRowsSection';
import { DynamicQuoteTable } from '../../../components/QuoteTable';
import { formatCurrency, getRowDisplayName } from '../../../components/QuoteTable/utils';
import { buildReadOnlyRows, buildReadOnlyTotals, calculateReadOnlyColumnTotals, cellDataToLookup, mapCellSnapshotToRows, mapQuoteTableRowsToReadOnly } from './utils';
import {
    calculateTotal,
    getClientRows,
    getSupplierRows,
    handleKeyDown,
    handleNumberValue,
    mapRowToSupplierRow,
    TABLE_HEADERS,
    TOTAL_ROWS,
} from './BidderQuoteTab.helpers';
import type { QuoteFieldValues, TotalType } from './BidderQuoteTab.helpers';
import { useSupplierRowTotals } from './hooks/useSupplierRowTotals';
import { useDraftRevisions } from './hooks/useDraftRevisions';
import { useQuoteValidation } from './hooks/useQuoteValidation';
import { OverallTotalsSummary } from './components/OverallTotalsSummary';
import type { QuoteTableColumn, SupplierRow } from '../../../components/QuoteTable/quote-table.types';
import { ReadOnlyQuoteTable } from './components/ReadOnlyQuoteTable';
import type { RfxQuoteItem, RfxQuoteTable } from '../../types/quote.type';
import type { QuoteRevisionSnapshot } from '../../types/revision.type';
import { RFX_SUBMISSION_STATUS } from '../../constants';
import { GST_RATE } from '../../../config/gst.config';
import { getQuoteRevisions, getSummaryTables } from '../../api';
import { SummaryTable } from '../../../components/QuoteTable/SummaryTable';
import type { SummaryTableConfig } from '../../types/quote.type';
import { UpdateQuoteModal } from './UpdateQuoteModal';
import { CompareRevisionsModal } from './CompareRevisionsModal';
import { LoadingSpinner } from 'components/Loading/LoadingSpinner';
import { ScheduleOfRates, ScheduleOfRatesHandle } from 'newui/features/Rfx/components/ScheduleOfRates';

export interface QuoteTabHandle {
    validateAndSave: () => Promise<boolean>;
}

export interface QuoteTabProps {
    disabled: boolean;
    inviteStatus: string;
    isClientView: boolean;
    listQuotes: RfxQuoteTable[];
    rfxId?: number;
    rfxInviteId?: number;
    onDraftRevisionChange?: (hasDraft: boolean) => void;
    onQuoteRevisionUpdated?: (isQuoteTabValid?: boolean) => void;
}

const BidderQuoteTab = forwardRef<QuoteTabHandle, QuoteTabProps>(({
    isClientView,
    listQuotes,
    disabled,
    inviteStatus,
    rfxId,
    rfxInviteId,
    onDraftRevisionChange,
    onQuoteRevisionUpdated,
}, ref) => {
    const [, { value: values }] = useField<QuoteFieldValues>('quote_answer');
    const { setFieldValue } = useFormikContext();
    const { isLoading: isUnitMeasuresLoading, values: unitsAndMeasures } = useUnitsAndMeasures();

    const supplierRowRefs = useRef<Record<number, SupplierRowsSectionHandle | null>>({});
    const scheduleRef = useRef<ScheduleOfRatesHandle>(null);
    const localIdCounterRef = useRef(1);
    const [summaryTables, setSummaryTables] = useState<SummaryTableConfig[]>([]);

    const {
        quoteValidationErrors,
        scheduleValidationError,
        clientRowErrors,
        validate,
        clearCellError,
    } = useQuoteValidation(listQuotes, values, isClientView, supplierRowRefs, scheduleRef);

    useImperativeHandle(ref, () => ({
        validateAndSave: async (): Promise<boolean> => {
            // Flush any supplier rows still pending in local state BEFORE we
            // validate or submit. The description-textarea onBlur is what
            // normally creates them; clicking Save races that handler and
            // unsaved rows would otherwise be dropped on refresh.
            const flushes = Object.values(supplierRowRefs.current)
                .filter((h): h is SupplierRowsSectionHandle => !!h)
                .map((h) => h.flushPending());
            await Promise.all(flushes);

            const isValid = validate();

            // Save schedule of rates
            if (scheduleRef.current) {
                const saved = await scheduleRef.current.save();
                if (!saved) {
                    return false;
                }
            }

            return isValid;
        },
    }), [validate]);

    const handleFormikFieldChange = useCallback((itemId: number, field: string, value: number | string) => {
        setFieldValue(`quote_answer.quote-${itemId}.${field}`, value);
        clearCellError(itemId, field, value);
    }, [setFieldValue, clearCellError]);

    const [showUpdateQuoteModal, setShowUpdateQuoteModal] = useState(false);
    const [updateQuoteData, setUpdateQuoteData] = useState<{
        current: null | QuoteRevisionSnapshot;
        previous: null | QuoteRevisionSnapshot;
        quote: null | RfxQuoteTable;
    }>({ current: null, previous: null, quote: null });
    const [showCompareModal, setShowCompareModal] = useState(false);
    const [compareQuote, setCompareQuote] = useState<RfxQuoteTable | null>(null);
    const { supplierRowCosts, supplierColTotals, handleSupplierRowsChange } =
        useSupplierRowTotals(listQuotes, rfxInviteId, values);

    const isBeingRevised = inviteStatus === RFX_SUBMISSION_STATUS.BEING_REVISED;

    const {
        draftRevisions,
        isQuotesLoading,
        refreshingQuoteId,
        setRefreshingQuoteId,
        quoteDiffKeys,
        fetchDraftRevisions,
        hasDrafts,
    } = useDraftRevisions(rfxInviteId, listQuotes, isBeingRevised, isClientView, disabled);

    useEffect(() => {
        onDraftRevisionChange?.(hasDrafts);
    }, [hasDrafts, onDraftRevisionChange]);

    // Intentionally do NOT auto-validate on initial load. Auto-validating made
    // freshly-reloaded data appear "invalid" with red cell highlights even when
    // the user had just saved successfully — validation rules are stricter than
    // what save accepts (save never blocks on empty cells), so the two
    // disagreed. Validation now runs only when the user clicks Save Progress
    // (via `validateAndSave` on the imperative handle).

    // Fetch summary tables for this RFx
    useEffect(() => {
        if (!rfxId) {
            return;
        }
        getSummaryTables(rfxId).then(setSummaryTables).catch(() => setSummaryTables([]));
    }, [rfxId]);

    // Compute overall totals, substituting draft revision figures when applicable
    const overallTotals = useMemo(() => {
        let subtotal = 0;

        listQuotes?.forEach((quote) => {
            const clientRows = getClientRows(quote.rows);
            const draftRevision = draftRevisions[quote.id];
            if (draftRevision && isBeingRevised && !isClientView) {
                // Draft revision: compute subtotal from cells (qty × rate per row)
                const cellLookup = cellDataToLookup(draftRevision.cells ?? []);
                const draftRows = mapCellSnapshotToRows(cellLookup, clientRows, quote.columns);
                subtotal += draftRows.reduce((acc, row) => acc + row.quantity * row.rate, 0);
            } else {
                // Build column-type lookup so we can treat saving as a flat
                // discount (independent of quantity) while response_value rows
                // multiply by quantity only when there is exactly 1 value column.
                const colTypes: Record<number, string> = {};
                quote.columns?.forEach((c) => { colTypes[c.id] = c.columnType; });
                const valueColCount = quote.columns?.filter((c) => c.columnType === 'response_value').length ?? 0;
                const hasQtyCol = quote.columns?.some((c) => c.columnType === 'response_quantity') ?? false;

                clientRows.forEach((row) => {
                    const key = `quote-${row.id}`;
                    const rowVals = values?.[key] ?? {};
                    const quantity = Number(rowVals.quantity ?? 0);

                    let responseRateSum = 0;
                    let savingFlat = 0;
                    Object.keys(rowVals).forEach((field) => {
                        if (!field.startsWith('rate_')) {
                            return;
                        }
                        const colId = Number(field.slice('rate_'.length));
                        const val = Number(rowVals[field] ?? 0);
                        if (colTypes[colId] === 'saving') {
                            savingFlat += val; // already negative
                        } else {
                            responseRateSum += val;
                        }
                    });

                    const legacyRate = Number(rowVals.rate ?? 0);
                    const effectiveRate = responseRateSum > 0 ? responseRateSum : legacyRate;
                    // Mirror QuoteTableCell: only multiply by qty when exactly 1 value column
                    const useMultiplier = valueColCount === 1 && hasQtyCol && quantity > 0;
                    subtotal += (useMultiplier ? effectiveRate * quantity : effectiveRate) + savingFlat;
                });
            }
            subtotal += supplierRowCosts[quote.id] ?? 0;
        });

        return {
            subtotal,
            gst: subtotal * GST_RATE,
            totalcost: subtotal + subtotal * GST_RATE,
        };
    }, [listQuotes, draftRevisions, isBeingRevised, isClientView, values, supplierRowCosts]);

    const hasAnyTotalColumn = useMemo(() => {
        return listQuotes?.some((q) => q.columns?.some((c) => c.columnType === 'total')) ?? false;
    }, [listQuotes]);

    // Build a map from quote table ID → { columnName → columnId } for response_value columns
    const quoteColumnNameToId = useMemo(() => {
        const result: Record<number, Record<string, number>> = {};
        listQuotes?.forEach((quote) => {
            const map: Record<string, number> = {};
            quote.columns
                ?.filter((c) => c.columnType === 'response_value')
                .forEach((c) => {
                    map[c.name] = c.id;
                });
            result[quote.id] = map;
        });
        return result;
    }, [listQuotes]);

    // Compute live cell values for summary tables from Formik state or draft revision cells
    const summaryLiveCellValues = useMemo(() => {
        if (!summaryTables.length || !listQuotes?.length) {
            return {};
        }
        const result: Record<number, Record<number, Record<string, number>>> = {};

        for (const st of summaryTables) {
            const perTable: Record<number, Record<string, number>> = {};

            for (const qtId of st.quote_table_ids) {
                const quote = listQuotes.find((q) => q.id === qtId);
                if (!quote) {
                    continue;
                }
                const columnMap = quoteColumnNameToId[qtId] ?? {};
                const perColumn: Record<string, number> = {};
                const draft = draftRevisions[qtId];
                const draftCellLookup = draft?.cells?.length
                    ? cellDataToLookup(draft.cells)
                    : null;

                // Collect all row IDs to sum: quote.rows plus any extra rows in the draft
                const knownRowIds = new Set(quote.rows.map((r) => r.id));
                const allRowIds = [...knownRowIds];
                if (draftCellLookup) {
                    for (const rowId of Object.keys(draftCellLookup).map(Number)) {
                        if (!knownRowIds.has(rowId)) {
                            allRowIds.push(rowId);
                        }
                    }
                }

                // Identify supplier row IDs so we can skip them in the Formik/draft loop
                // (their values are tracked separately via supplierColTotals)
                const supplierRowIds = new Set(
                    getSupplierRows(quote.rows, rfxInviteId ?? 0).map((r) => r.id)
                );

                for (const colName of st.value_column_names) {
                    const colId = columnMap[colName];
                    if (colId === undefined) {
                        continue;
                    }
                    // Sum client rows from Formik/draft
                    let sum = 0;
                    for (const rowId of allRowIds) {
                        if (supplierRowIds.has(rowId)) {
                            continue;
                        }
                        let val: number;
                        if (draftCellLookup && draftCellLookup[rowId] !== undefined) {
                            val = Number(draftCellLookup[rowId]?.[colId] ?? 0);
                        } else {
                            const key = `quote-${rowId}`;
                            const rowValues = values?.[key];
                            val = Number(rowValues?.[`rate_${colId}`] ?? 0);
                        }
                        if (!isNaN(val)) {
                            sum += val;
                        }
                    }
                    // Add supplier row totals (managed by SupplierRowsSection)
                    sum += supplierColTotals[qtId]?.[colId] ?? 0;
                    perColumn[colName] = sum;
                }

                perTable[qtId] = perColumn;
            }

            result[st.id] = perTable;
        }

        return result;
    }, [summaryTables, listQuotes, quoteColumnNameToId, values, draftRevisions, supplierColTotals, rfxInviteId]);

    const createRevision = (snapshot: QuoteRevisionSnapshot) => {
        const newRevision = snapshot.revision + 1;
        return { ...snapshot, label: `Revision ${newRevision}`, is_submitted: false, revision: newRevision };
    }

    const handleOpenReviseQuote = useCallback(async (quote: RfxQuoteTable) => {
        if (!rfxInviteId) {
            return;
        }
        try {
            const revisions = await getQuoteRevisions({ inviteId: rfxInviteId, quoteId: quote.id });
            const sorted = [...revisions].sort((a, b) => b.revision - a.revision);

            const isLatestSubmitted = sorted[0].is_submitted;

            // if latest revision has been submitted, we need a new current submission
            const currentRevision = isLatestSubmitted ? createRevision(sorted[0]) : sorted[0];

            // if latest has been submitted, its the previous, otherwise its the one before this
            const previousRevision = isLatestSubmitted ? sorted[0] : sorted[1];

            if (currentRevision && previousRevision) {
                setUpdateQuoteData({ current: currentRevision, previous: previousRevision, quote });
                setShowUpdateQuoteModal(true);
            }
        } catch {
            // Error handled by global interceptor
        }
    }, [rfxInviteId]);

    const unitLookup = useMemo(() => {
        if (!unitsAndMeasures?.length) {
            return undefined;
        }
        return new Map(unitsAndMeasures.map((u) => [u.value, u.label]));
    }, [unitsAndMeasures]);

    const getQuoteComment = (quote: RfxQuoteTable): string => {
        // Comments are eager-loaded and scoped to the current invite by the backend
        return quote.comments?.[0]?.comment ?? "";
    };

    const getQuoteAttachments = (quote: RfxQuoteTable) => {
        // Attachments are eager-loaded and scoped to the current invite by the backend
        return (quote.attachments ?? []).map((a) => ({
            id: a.id,
            file_name: a.file_name,
            file_path: a.file_path,
            file_size: a.file_size,
            file_type: a.file_type,
        }));
    };

    if (isQuotesLoading) {
        return <LoadingSpinner />
    }

    return (
        <>
            {showUpdateQuoteModal && updateQuoteData.current && updateQuoteData.previous && updateQuoteData.quote && (
                <UpdateQuoteModal
                    isOpen={showUpdateQuoteModal}
                    onClose={() => setShowUpdateQuoteModal(false)}
                    inviteId={rfxInviteId}
                    currentData={updateQuoteData.current}
                    previousData={updateQuoteData.previous}
                    quote={updateQuoteData.quote}
                    onSaveSuccess={(isQuoteTabValid?: boolean) => {
                        onQuoteRevisionUpdated?.(isQuoteTabValid);
                        setRefreshingQuoteId(updateQuoteData.quote?.id ?? null);
                        fetchDraftRevisions();
                    }}
                />
            )}
            {showCompareModal && compareQuote && rfxInviteId && (
                <CompareRevisionsModal
                    isOpen={showCompareModal}
                    onClose={() => setShowCompareModal(false)}
                    columns={compareQuote.columns}
                    inviteId={rfxInviteId}
                    quoteId={compareQuote.id}
                    quoteName={compareQuote.name}
                    quoteItems={compareQuote.rows}
                    initialRevisionA={compareQuote.current_revision}
                    initialRevisionB={compareQuote.current_revision > 0 ? compareQuote.current_revision - 1 : 0}
                />
            )}
            {listQuotes?.map((quote) => {
                const draftRevision = draftRevisions[quote.id];
                const totalRevisions = draftRevision
                    ? draftRevision.revision
                    : quote.current_revision;
                const clientRows = getClientRows(quote.rows);
                const supplierRows = getSupplierRows(quote.rows, rfxInviteId);
                const initialSupplierRows: SupplierRow[] = supplierRows.map((r) => mapRowToSupplierRow(r, values, quote.columns, localIdCounterRef));

                return (
                    <RfxQuoteHeader
                        key={`quote-section-${quote.id}`}
                        isBeingRevised={!isClientView && isBeingRevised}
                        hasRevisions={quote.has_revisions}
                        hasValidationError={quoteValidationErrors.has(quote.id)}
                        onOpenReviseQuote={handleOpenReviseQuote}
                        onCompareRevisions={(q) => {
                            setCompareQuote(q);
                            setShowCompareModal(true);
                        }}
                        quote={quote}
                    >
                        {quote.has_revisions && !draftRevision && (
                            <Flex
                                className="mb-3"
                                align="center"
                                gap={2}
                            >
                                <Badge
                                    colorScheme="blue"
                                    data-testid={`revision-badge-${quote.id}`}
                                >
                                    Revision {quote.current_revision} of {totalRevisions}
                                </Badge>
                            </Flex>
                        )}
                        {draftRevision && isBeingRevised && (
                            <Flex
                                className="mb-3"
                                align="center"
                                gap={2}
                            >
                                <Badge
                                    colorScheme="orange"
                                    data-testid={`draft-revision-badge-${quote.id}`}
                                >
                                    Revision {draftRevision.revision}
                                </Badge>
                            </Flex>
                        )}
                        {disabled ? (
                            (() => {
                                const cols: QuoteTableColumn[] = (quote.columns ?? []).map((c) => ({
                                    id: c.id,
                                    columnType: c.columnType,
                                    label: c.name,
                                    sortOrder: c.sortOrder,
                                    isDefault: c.isClientEditable,
                                    isSupplierEditable: c.isSupplierEditable,
                                }));

                                // When a draft revision exists, show its values instead of Formik values
                                let allRows;
                                if (draftRevision) {
                                    const cellLookup = cellDataToLookup(draftRevision.cells ?? []);
                                    const draftRows = mapCellSnapshotToRows(cellLookup, clientRows, quote.columns);
                                    allRows = mapQuoteTableRowsToReadOnly(draftRows, cols);
                                } else {
                                    allRows = [
                                        ...buildReadOnlyRows(clientRows, cols, values, unitLookup),
                                        ...buildReadOnlyRows(supplierRows, cols, values, unitLookup, true),
                                    ];
                                }

                                // Σ per-column totals (when procurer enabled includeTotalsRow)
                                let colTotals: Record<number, number> | null = null;
                                if (quote.includeTotalsRow) {
                                    if (draftRevision?.cells?.length) {
                                        // Compute from draft cells; supplier rows without draft cells use supplierColTotals
                                        const draftLookup = cellDataToLookup(draftRevision.cells);
                                        const responseValueCols = cols.filter((c) => c.columnType === 'response_value');
                                        const supplierRowIdSet = new Set(supplierRows.map((r) => r.id));
                                        const totalsMap: Record<number, number> = {};
                                        for (const col of responseValueCols) {
                                            let sum = 0;
                                            let supplierSummedFromDraft = false;
                                            for (const row of [...clientRows, ...supplierRows]) {
                                                if (draftLookup[row.id] !== undefined) {
                                                    const val = Number(draftLookup[row.id]?.[col.id] ?? 0);
                                                    if (!isNaN(val)) {
                                                        sum += val;
                                                    }
                                                    if (supplierRowIdSet.has(row.id)) {
                                                        supplierSummedFromDraft = true;
                                                    }
                                                } else if (!supplierRowIdSet.has(row.id)) {
                                                    // Client row not in draft — read from Formik
                                                    const formikVal = values?.[`quote-${row.id}`]?.[`rate_${col.id}`];
                                                    const val = Number(formikVal ?? 0);
                                                    if (!isNaN(val)) {
                                                        sum += val;
                                                    }
                                                }
                                            }
                                            // If no supplier rows were in the draft, add from supplierColTotals
                                            if (!supplierSummedFromDraft) {
                                                sum += supplierColTotals[quote.id]?.[col.id] ?? 0;
                                            }
                                            totalsMap[col.id] = sum;
                                        }
                                        colTotals = totalsMap;
                                    } else {
                                        colTotals = calculateReadOnlyColumnTotals(
                                            cols,
                                            clientRows,
                                            values,
                                            supplierColTotals[quote.id]
                                        );
                                    }
                                }

                                // Summary rows (only when table has a 'total' column type)
                                const hasTotalColumn = cols.some((c) => c.columnType === 'total');
                                const subtotal = hasTotalColumn
                                    ? calculateTotal('subtotal', 'all', values, supplierRowCosts[quote.id] ?? 0)
                                    : 0;

                                const isRefreshing = refreshingQuoteId === quote.id;

                                return (
                                    <div className="relative">
                                        {isRefreshing && (
                                            <div className="absolute inset-0 z-10 flex items-center justify-center rounded-xl bg-white/70">
                                                <Spinner size="lg" color="blue.500" />
                                            </div>
                                        )}
                                        <ReadOnlyQuoteTable
                                            columns={cols}
                                            columnTotals={colTotals}
                                            rows={allRows}
                                            diffKeys={quoteDiffKeys[quote.id]}
                                            totals={hasTotalColumn ? buildReadOnlyTotals(subtotal) : undefined}
                                        />
                                    </div>
                                );
                            })()
                        ) : quote.columns && quote.columns.length > 0 ? (
                            <DynamicQuoteTable
                                columns={quote.columns.map((c) => ({
                                    id: c.id,
                                    columnType: c.columnType,
                                    label: c.name,
                                    sortOrder: c.sortOrder,
                                    isDefault: c.isClientEditable,
                                    isSupplierEditable: c.isSupplierEditable,
                                }))}
                                disabled={disabled}
                                includeTotalsRow={quote.includeTotalsRow === true}
                                items={clientRows}
                                mode={isClientView ? "view" : "respond"}
                                supplierColumnTotals={supplierColTotals[quote.id]}
                                allValues={values}
                                rowErrors={clientRowErrors}
                                onFormikFieldChange={handleFormikFieldChange}
                                supplierRowsRef={rfxInviteId ? (handle: SupplierRowsSectionHandle | null) => { supplierRowRefs.current[quote.id] = handle; } : undefined}
                                supplierRowsProps={rfxInviteId ? {
                                    columns: quote.columns.map((c) => ({
                                        id: c.id,
                                        columnType: c.columnType,
                                        label: c.name,
                                        sortOrder: c.sortOrder,
                                        isDefault: c.isClientEditable,
                                        isSupplierEditable: c.isSupplierEditable,
                                    })),
                                    inviteId: rfxInviteId,
                                    quoteId: quote.id,
                                    startIndex: clientRows.length,
                                    initialRows: initialSupplierRows,
                                    onRowsChange: (rows: SupplierRow[]) => handleSupplierRowsChange(quote.id, rows),
                                    onFormikFieldChange: handleFormikFieldChange,
                                } : undefined}
                                totals={quote.columns?.some((c) => c.columnType === 'total')
                                    ? TOTAL_ROWS.map((row) => ({
                                        id: row.id,
                                        name: row.name,
                                        color: row.color,
                                        value: calculateTotal(
                                            row.id,
                                            clientRows.map((r) => `quote-${r.id}`),
                                            values,
                                            supplierRowCosts[quote.id] ?? 0,
                                            quote.columns,
                                        ),
                                    }))
                                    : []}
                            />
                        ) : (
                            <table className="w-full table-auto rounded-xl border-b border-solid border-[#EAECF0] bg-white">
                                <thead className="h-[44px] bg-[#F9FAFB]">
                                    <tr className="border-b border-[#EAECF0]">
                                        {TABLE_HEADERS.map((th) => (
                                            <th
                                                key={th.name}
                                                className="bg-[#F9FAFB] text-left font-roboto text-[12px] font-medium text-tertiary-600"
                                                style={{ width: th.width }}
                                            >
                                                <div className="ml-5 mt-[3px]">{th.name}</div>
                                            </th>
                                        ))}
                                    </tr>
                                </thead>
                                <tbody>
                                    {clientRows.map((singleData, itemIndex) => {
                                        const isPercentage = singleData.unitId === 13;
                                        return <tr
                                            key={`quote-item-${singleData.id}`}
                                            className="border-b border-[#EAECF0] hover:bg-blue-200"
                                        >
                                            <td
                                                className="border border-[#EAECF0] px-4 py-2 text-center font-roboto font-normal text-primary-500"
                                                style={{ background: '#F9FAFB', height: '48px' }}
                                            >
                                                {itemIndex + 1}
                                            </td>
                                            <td
                                                className="relative border border-[#EAECF0] font-roboto"
                                                style={{ color: '#333', backgroundColor: '#fafafa' }}
                                            >
                                                <Field
                                                    name={`quote_answer[quote-${singleData.id}][name]`}
                                                >
                                                    {() => (
                                                        <p className="p-2">{getRowDisplayName(singleData, quote.columns) || '-'}</p>
                                                    )}
                                                </Field>
                                            </td>
                                            <td
                                                className="relative border border-[#EAECF0] font-roboto"
                                                style={{ color: '#333', backgroundColor: '#fafafa' }}
                                            >
                                                {isUnitMeasuresLoading && (
                                                    <Spinner
                                                        size="sm"
                                                        className="ml-2 mt-1"
                                                    />
                                                )}
                                                {!isUnitMeasuresLoading && (
                                                    <Field
                                                        name={`quote_answer[quote-${singleData.id}][unit]`}
                                                    >
                                                        {({ field }: { field: { value: number } }) => (
                                                            <p className="px-2">
                                                                {
                                                                    unitsAndMeasures?.find(
                                                                        (unit) => unit.value === field?.value
                                                                    )?.label
                                                                }
                                                            </p>
                                                        )}
                                                    </Field>
                                                )}
                                            </td>
                                            <td
                                                className="relative border border-[#EAECF0] px-4 py-2 font-roboto"
                                                style={{ color: '#333' }}
                                            >
                                                <Field
                                                    name={`quote_answer[quote-${singleData.id}][quantity]`}
                                                >
                                                    {({
                                                        field,
                                                        meta,
                                                        form,
                                                    }: {
                                                        field: React.InputHTMLAttributes<HTMLInputElement> & { name: string };
                                                        form: { setFieldValue: (name: string, value: number | string) => void };
                                                        meta: { error?: string; touched: boolean; };
                                                    }) => (
                                                        <input
                                                            type="number"
                                                            {...field}
                                                            value={handleNumberValue(field.value as string)}
                                                            onChange={(e) =>
                                                                form.setFieldValue(
                                                                    field.name,
                                                                    handleNumberValue(e.target.value)
                                                                )
                                                            }
                                                            className="absolute left-0 top-0 h-full w-full px-5 py-[5px] outline-none"
                                                            style={{
                                                                border:
                                                                    meta.touched && meta.error
                                                                        ? '2px solid #dd0c0cb3'
                                                                        : undefined,
                                                            }}
                                                            placeholder="0"
                                                            min={0}
                                                            onKeyDown={handleKeyDown}
                                                            disabled={disabled}
                                                        />
                                                    )}
                                                </Field>
                                            </td>
                                            <td
                                                className="relative border border-[#EAECF0] px-4 py-2 font-roboto"
                                                style={{ color: '#333' }}
                                            >
                                                <div className="flex">
                                                    {!isPercentage && <div className="absolute left-2 top-1/2 z-10 -translate-y-1/2">
                                                        $
                                                    </div>}
                                                    <Field
                                                        name={`quote_answer[quote-${singleData.id}][rate]`}
                                                    >
                                                        {({
                                                            field,
                                                            meta,
                                                            form,
                                                        }: {
                                                            field: React.InputHTMLAttributes<HTMLInputElement> & { name: string };
                                                            form: { setFieldValue: (name: string, value: number | string) => void };
                                                            meta: { error?: string; touched: boolean; };
                                                        }) => (
                                                            isPercentage ? <></> :
                                                                <input
                                                                    type="number"
                                                                    {...field}
                                                                    value={handleNumberValue(field.value as string)}
                                                                    onChange={(e) =>
                                                                        form.setFieldValue(
                                                                            field.name,
                                                                            handleNumberValue(e.target.value)
                                                                        )
                                                                    }
                                                                    className="absolute left-0 right-0 top-0 h-full w-full px-5 py-[5px] pl-5 outline-none"
                                                                    style={{
                                                                        border:
                                                                            meta.touched && meta.error
                                                                                ? '2px solid #dd0c0cb3'
                                                                                : undefined,
                                                                    }}
                                                                    placeholder="0"
                                                                    min={0}
                                                                    step={1}
                                                                    onKeyDown={handleKeyDown}
                                                                    disabled={disabled}
                                                                />
                                                        )}
                                                    </Field>
                                                </div>
                                            </td>
                                            <td
                                                className="relative border border-[#EAECF0] px-4 py-2 font-roboto"
                                                style={{ color: '#333' }}
                                            >
                                                <Field
                                                    name={`quote_answer[quote-${singleData.id}][cost]`}
                                                    disabled={true}
                                                >
                                                    {() => (
                                                        isPercentage ? '-' :
                                                            <input
                                                                type="text"
                                                                readOnly
                                                                disabled
                                                                className="absolute left-0 top-0 h-full w-full px-5 py-[5px]"
                                                                value={formatCurrency(
                                                                    (Number(
                                                                        values?.[`quote-${singleData.id}`]
                                                                            ?.quantity ?? 0
                                                                    ) *
                                                                        Number(
                                                                            values?.[`quote-${singleData.id}`]
                                                                                ?.rate ?? 0
                                                                        ))
                                                                )}
                                                            />
                                                    )}
                                                </Field>
                                            </td>
                                        </tr>
                                    })}
                                </tbody>
                                {rfxInviteId && (
                                    <SupplierRowsSection
                                        ref={(handle) => { supplierRowRefs.current[quote.id] = handle; }}
                                        disabled={disabled}
                                        inviteId={rfxInviteId}
                                        isClientView={isClientView}
                                        quoteId={quote.id}
                                        startIndex={clientRows.length}
                                        initialRows={initialSupplierRows}
                                        onRowsChange={(rows) => handleSupplierRowsChange(quote.id, rows)}
                                        onFormikFieldChange={handleFormikFieldChange}
                                    />
                                )}
                                <tbody>
                                    {TOTAL_ROWS.map((list) => (
                                        <tr key={list.id}>
                                            <td
                                                colSpan={isBeingRevised ? 5 : 4}
                                                className="h-[48px] border-none"
                                                style={{ background: '#F9FAFB' }}
                                            />
                                            <td
                                                className="border border-[#EAECF0] bg-gray-utility-100 px-4 py-2"
                                                style={{ color: '#D0D5DD' }}
                                            >
                                                <div
                                                    className="font-roboto font-medium"
                                                    style={{ fontSize: '12px', color: list.color }}
                                                >
                                                    {list.name}
                                                </div>
                                            </td>
                                            <td
                                                className="border border-[#EAECF0] bg-gray-utility-100 px-4 py-2"
                                                style={{ color: '#333' }}
                                            >
                                                {formatCurrency(
                                                    calculateTotal(
                                                        list.id,
                                                        clientRows.map((r) => `quote-${r.id}`),
                                                        values,
                                                        supplierRowCosts[quote.id] ?? 0
                                                    )
                                                )}
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        )}
                        {rfxInviteId && (
                            <QuoteComment
                                initialComment={getQuoteComment(quote)}
                                inviteId={rfxInviteId}
                                isReadOnly={disabled || isClientView}
                                quoteId={quote.id}
                            />
                        )}
                        {rfxInviteId && (
                            <QuoteSupportingDocuments
                                initialDocuments={getQuoteAttachments(quote)}
                                inviteId={rfxInviteId}
                                isReadOnly={disabled || isClientView}
                                quoteId={quote.id}
                            />
                        )}
                    </RfxQuoteHeader>
                );
            })}
            {summaryTables.length > 0 && rfxId && (
                <div className="mt-6 space-y-6">
                    {summaryTables.map((st) => (
                        <SummaryTable
                            key={st.id}
                            categoryNames={st.value_column_names}
                            inviteId={rfxInviteId}
                            liveCellValues={summaryLiveCellValues[st.id]}
                            mode={isClientView ? "view" : "respond"}
                            quoteTableIds={st.quote_table_ids}
                            quoteTableNames={listQuotes?.reduce((acc, q) => {
                                acc[q.id] = q.name;
                                return acc;
                            }, {} as Record<number, string>) ?? {}}
                            rfxId={rfxId}
                            summaryTableId={st.id}
                            summaryTableName={st.name}
                        />
                    ))}
                </div>
            )}
            {hasAnyTotalColumn && (
                <OverallTotalsSummary
                    subtotal={overallTotals.subtotal}
                    gst={overallTotals.gst}
                    totalcost={overallTotals.totalcost}
                />
            )}
            {rfxInviteId && (
                <ScheduleOfRates
                    ref={scheduleRef}
                    inviteId={rfxInviteId}
                    disabled={disabled}
                    hasValidationError={scheduleValidationError}
                    isClientView={isClientView}
                />
            )}
        </>
    );
});

BidderQuoteTab.displayName = "QuoteTab";

export { BidderQuoteTab as QuoteTab };
